import { Button, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { LearningItem } from "api/resources/learningItems/types"
import { useNavigate } from "react-router-dom"
import routes from "routes"
import { cpatType } from "utils/constants"
import { useEnrollments } from "utils/hooks/useEnrollments"

import TitleCard from "./TitleCard"

const CPAT_INSTRUCTIONS = [
  "The Comprehensive Pedagogy Assessment of Teachers (CPAT) is an exciting opportunity for you to assess your pedagogical skills.",
  "During a one-hour online assessment, you get the opportunity to put your expertise to the test.",
  "Based on your performance, you receive a customized pedagogy report, highlighting what you're good at and where you can improve.",
  "This customized feedback guides you toward becoming an even more effective educator.",
]

const CpatStep = ({ item }: { item: LearningItem }) => {
  if (item.learningContentType !== "cpat")
    throw new Error("Invalid content type passed to CpatStep component")

  const navigate = useNavigate()
  const { cpat } = useEnrollments()

  const progress = cpat.latestCPAT?.progress ?? 0
  const isReportReady = progress >= 100

  const reports = useQuery({
    queryKey: ["cpat-reports"],
    queryFn: () => api.cpat.getReports({ queryParams: { types: cpatType } }),
    enabled: isReportReady,
  })
  const reportFile = reports.data?.[0]?.file

  const label = isReportReady
    ? "View Report"
    : progress > 0
    ? "Continue CPAT"
    : "Go to CPAT"

  const handleClick = async () => {
    if (!isReportReady) {
      navigate(routes.cpat.dashboard)
      return
    }

    // On a fresh page load the report query may not have resolved yet by
    // the time the learner clicks, so make sure we have the latest data
    // before deciding there's no file to open.
    const file = reportFile ?? (await reports.refetch()).data?.[0]?.file
    if (file) {
      window.open(file, "_blank")
      return
    }
    navigate(routes.cpat.dashboard)
  }

  return (
    <div className="mb-6">
      <TitleCard
        title={item.title}
        subtitle="Comprehensive Pedagogy Assessment of Teachers"
        action={
          <Button
            color="black"
            onClick={handleClick}
            loading={isReportReady && reports.isFetching}
          >
            {label}
          </Button>
        }
      />
      <Typography variant="title4" className="my-2">
        Instructions
      </Typography>
      <ul className="mb-10 list-disc space-y-1 pl-4">
        {CPAT_INSTRUCTIONS.map(point => (
          <li key={point}>
            <Typography>{point}</Typography>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default CpatStep
